import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { parseISO } from 'date-fns';
import { preferImperialQuantity } from './quantity';

/** A single point on a measurement trend line. `y2` carries the second value of composite readings (e.g. BP diastolic). */
export type TrendPoint = { x: number; y: number; y2?: number };

// Sleep durations are stored in minutes by every producer, so plotting the raw stored value keeps a
// mixed-producer series on one scale. Non-sleep measurements likewise plot their raw stored value.
const parseTrendValues = (observation: Observation): { y: number | null; y2?: number } => {
  const value = preferImperialQuantity(observation.valueQuantity)?.value;
  if (value !== undefined) return { y: value };

  const componentValues = observation.component
    ?.map((c) => preferImperialQuantity(c.valueQuantity)?.value)
    .filter((v): v is number => v !== undefined);

  return { y: componentValues?.[0] ?? null, y2: componentValues?.[1] };
};

/**
 * Build sorted numeric trend points from observations, preferring imperial units and handling
 * composite (e.g. blood-pressure) component values. Shared by patient-web's sparkline and detailed
 * chart and by patient-native's chart.
 */
export const toTrendSeries = (results: WithId<Observation>[]): { data: TrendPoint[]; hasSecondLine: boolean } => {
  const data = results
    .map((r) => ({ x: parseISO(r.effectiveDateTime ?? '').getTime(), ...parseTrendValues(r) }))
    .filter((p): p is TrendPoint => p.y !== null && !isNaN(p.x))
    .sort((a, b) => a.x - b.x);
  return { data, hasSecondLine: data.some((p) => p.y2 !== undefined) };
};
